import { BottomDrawerModalAnimated } from "@/components/shared/bottom-drawer-modal-animated";
import { Button, Image, View } from "@/components/ui";
import { RateVisitInput } from "../rate-visit-input";
import { IconButton } from "@/components/ui/icon-button";
import { ImagePreviewInput } from "@/components/shared/image-preview-input";
import type { VisitDetailData } from "@/components/shared/visits/visit-detail";
import { Divider } from "@/components/shared/divider";
import type { ImageFile } from "@/types/app";
import { Typography } from "@/components/shared/typography";
import { useRateVisitForm } from "@/store/visits/forms/useRateVisitForm";
import { useRateVisitStore } from "@/store/visits/useRateVisit";
import { useRateVisitWithTokenStore } from "@/store/visits/useRateVisitWithToken";
import { useVisitDetailStore } from "@/store/visits/visitDetail";
import { ClientInstance } from "@/lib/graphql/apollo";
import { useState } from "react";
import { packageServiceTypeLabel } from "@/lib/service/helpers";
import { formatVisitSchedule } from "@/components/shared/visits/utils";
import { showToast } from "@/components/ui/toast/show-toast";
import { getErrorMessage } from "@/lib/helpers/string";
import { VisitStatusBadge } from "@/components/shared/visits/visit-card/visit-status-badge";
import { Grid } from "@/components/shared/grid";

interface RateVisitFormProps {
  visitData: VisitDetailData;
  onSuccess?: (rating: number) => void;
}

export function RateVisitForm({ visitData, onSuccess }: RateVisitFormProps) {
  const [open, setOpen] = useState(true);

  const closeRateVisitModal = useVisitDetailStore((state) => {
    return state.closeRateVisitModal;
  });

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      closeRateVisitModal();
    }, 300);
  };

  const [reviewImages, setReviewImages] = useState<(string | File)[]>(
    visitData.reviewImageUrls,
  );

  const [files, setFiles] = useState<File[]>([]);

  const { fetch: rateVisitWIthToken, loading: loadingRateWithTokenMutation } =
    useRateVisitWithTokenStore();

  const { fetch: rateVisit, loading: loadingRateMutation } =
    useRateVisitStore();

  const rateVisitForm = useRateVisitForm({
    rating: 0,
    comment: "",
  });

  const handleSubmit = rateVisitForm.handleSubmit(async (values) => {
    const commonInput = {
      rating: values.rating,
      ratingComment: values.comment,
      files,
    };

    let res;
    try {
      if (visitData.token) {
        const requestPayload = {
          requestPayload: {
            input: {
              token: visitData.token,
              ...commonInput,
            },
          },
        };
        res = await rateVisitWIthToken(requestPayload);
      } else {
        const requestPayload = {
          requestPayload: {
            input: {
              visitId: visitData.id,
              ...commonInput,
            },
          },
        };
        res = await rateVisit(requestPayload);
      }
    } catch (networkError) {
      console.error("🚨 Network request failed with exception:", networkError);
      showToast({
        type: "error",
        title: getErrorMessage(
          networkError,
          "Something went wrong. Please try again.",
        ),
      });
      return;
    }

    if (res.error) {
      console.error("❌ API returned error:", res.error);
      console.error("❌ Full error response:", res);
      showToast({
        type: "error",
        title: getErrorMessage(
          res.error,
          "Failed to submit rating. Please try again.",
        ),
      });
      return;
    }

    // Evict visit list, individual visit, and token visit queries from cache to trigger refetch
    try {
      ClientInstance.cache.evict({
        fieldName: "visitsByFilters",
      });
      ClientInstance.cache.evict({
        fieldName: "visit",
      });
      ClientInstance.cache.evict({
        fieldName: "visitByClientWithToken",
      });
      ClientInstance.cache.gc();
    } catch (cacheError) {
      console.warn("⚠️ Cache eviction failed:", cacheError);
    }

    onSuccess?.(values.rating);
  });

  const handleRemoveImage = (imageUrl: string | File, index: number) => {
    setReviewImages((images) => {
      return images.filter((url, imgIndex) => {
        if (typeof url === "string") {
          return url !== imageUrl;
        } else {
          return index !== imgIndex;
        }
      });
    });
  };

  const handleFileChange = ({ file, uri }: ImageFile) => {
    setFiles((prevFiles) => [...prevFiles, file]);
    setReviewImages((prevImages) => [...prevImages, uri]);
  };

  return (
    <>
      <BottomDrawerModalAnimated
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose();
        }}
        title={
          <View className="flex flex-row items-center justify-between gap-2">
            <Typography variant="label-md">Your Review</Typography>
            <IconButton
              variant="ghost"
              color="foreground"
              size="md"
              iconName="x"
              onClick={handleClose}
              className="native:hidden"
            />
          </View>
        }
        footer={
          <>
            <View className="flex w-full flex-1 flex-col">
              <Button
                variant="primary"
                color="CTA"
                onClick={handleSubmit}
                loading={loadingRateMutation || loadingRateWithTokenMutation}
                children="Rate it!"
              />
            </View>
            <Divider className="my-4" />
          </>
        }
        className="native:max-h-[60vh] gap-0"
      >
        <Divider className="h-px bg-border" />
        <View className="my-2 flex flex-row items-start justify-between gap-2 rounded-lg border border-border px-6 py-4">
          <View className="flex flex-col gap-2">
            <Typography variant="label-sm">
              {packageServiceTypeLabel(
                visitData.serviceType,
                visitData.department,
              )}
            </Typography>
            <Typography variant="body-sm" color="foreground-intermediate">
              {formatVisitSchedule(
                visitData.startTime,
                visitData.endTime,
                visitData.serviceDate,
              )}
            </Typography>
          </View>
          <VisitStatusBadge status={visitData.status} />
        </View>
        <Divider className="h-1 bg-background-intermediate" />
        <RateVisitInput
          control={rateVisitForm.control}
          readonly={loadingRateMutation || loadingRateWithTokenMutation}
        />
        <Divider className="h-1 bg-background-intermediate" />
        <View className="flex flex-col gap-6 py-6">
          <Typography variant="label-sm">Show us your experience!</Typography>
          <Grid
            columns={3}
            gap={4}
            data={[
              ...reviewImages.map((image, index) => ({
                type: "image" as const,
                image,
                index,
              })),
              ...(reviewImages.length < 3 ? [{ type: "add" as const }] : []),
            ]}
            render={(item) =>
              item.type === "image" ? (
                <View className="relative flex flex-col gap-1">
                  <View className="aspect-square cursor-pointer overflow-hidden rounded-2xl object-cover">
                    <Image
                      src={item.image as string}
                      alt={`review image ${item.index + 1}`}
                      contentFit="cover"
                      className="h-full w-full object-cover"
                    />
                  </View>
                  <IconButton
                    variant="tertiary"
                    color="danger"
                    size="md"
                    iconName="minusCircle"
                    className="absolute -right-3 -top-3 h-6 w-6 rounded-full p-0"
                    onClick={() => handleRemoveImage(item.image, item.index)}
                  />
                </View>
              ) : (
                <View className="flex w-full flex-col gap-1">
                  <ImagePreviewInput onChange={handleFileChange} />
                  <Typography variant="caption-md" align="center">
                    Add Photo
                  </Typography>
                </View>
              )
            }
          />
        </View>
      </BottomDrawerModalAnimated>
    </>
  );
}
